import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Select,
  Spinner,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Divider,
  Input,
  useToast,
  Tag,
  Checkbox,
  Stack,
  Switch,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import xhr from "xhr";
import { useDropzone } from "react-dropzone";
import { BiCloudUpload } from "react-icons/bi";

export default function StudentAbsencesModal({
  isOpen,
  onClose,
  studentId,
  gradeId,
  programTypeId,
  programName,
}) {
  const toast = useToast();
  const now = useMemo(() => dayjs(), []);

  const [yearInput, setYearInput] = useState(String(now.year()));
  const [monthInput, setMonthInput] = useState(String(now.month() + 1));
  const [studentClassGroupIds, setStudentClassGroupIds] = useState([]);
  const [loadingStudentGroups, setLoadingStudentGroups] = useState(false);

  const [appliedYear, setAppliedYear] = useState(String(now.year()));
  const [appliedMonth, setAppliedMonth] = useState(String(now.month() + 1));

  const [loading, setLoading] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [deletingAbsId, setDeletingAbsId] = useState(null);

  const [excuses, setExcuses] = useState([]);
  const [loadingExcuses, setLoadingExcuses] = useState(false);

  // Contexto para live-classes (semester + class_group)
  const [ctx, setCtx] = useState(null);
  const [loadingCtx, setLoadingCtx] = useState(false);

  // ✅ Live classes del DÍA seleccionado en el form (max ~3)
  const [liveClassesDay, setLiveClassesDay] = useState([]);
  const [loadingLiveClassesDay, setLoadingLiveClassesDay] = useState(false);

  // ✅ Formulario de excusa por clases (multi-día)
  const [showExcuseForm, setShowExcuseForm] = useState(false);
  const [excuseDescription, setExcuseDescription] = useState("");
  const [excuseDay, setExcuseDay] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [selectedClassIds, setSelectedClassIds] = useState(() => new Set());
  const [selectedClassesMap, setSelectedClassesMap] = useState(() => new Map()); // id -> classObj
  const [savingExcuse, setSavingExcuse] = useState(false);
  const [isCameraExcuse, setIsCameraExcuse] = useState(false);

  const [excuseFile, setExcuseFile] = useState(null);

  const onDropExcuseFile = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles?.[0];

    if (!selectedFile) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(selectedFile.type)) {
      toast({
        title: "Archivo no válido",
        description: "Solo puedes subir archivos PDF o imágenes JPG, PNG o WEBP.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setExcuseFile(selectedFile);
  }, [toast]);

  const {
    getRootProps: getExcuseRootProps,
    getInputProps: getExcuseInputProps,
  } = useDropzone({
    onDrop: onDropExcuseFile,
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    multiple: false,
  });

  const yearsOptions = useMemo(() => {
    const y = dayjs().year();
    const out = [];
    for (let i = y - 6; i <= y + 1; i++) out.push(String(i));
    return out;
  }, []);

  const monthsOptions = useMemo(
    () => [
      { value: "1", label: "Enero" },
      { value: "2", label: "Febrero" },
      { value: "3", label: "Marzo" },
      { value: "4", label: "Abril" },
      { value: "5", label: "Mayo" },
      { value: "6", label: "Junio" },
      { value: "7", label: "Julio" },
      { value: "8", label: "Agosto" },
      { value: "9", label: "Septiembre" },
      { value: "10", label: "Octubre" },
      { value: "11", label: "Noviembre" },
      { value: "12", label: "Diciembre" },
    ],
    []
  );

  const normalizeLiveClass = (item) => {
    const a = item?.attributes || item;

    const lc = a?.live_class?.data?.attributes
      ? { id: a.live_class.data.id, ...a.live_class.data.attributes }
      : a?.live_class || null;

    const subject = lc?.subject?.data?.attributes
      ? { id: lc.subject.data.id, ...lc.subject.data.attributes }
      : lc?.subject || null;

    return { lc, subject };
  };

  const normalizeEntity = (item) => {
    if (item?.attributes) return item.attributes;
    return item || {};
  };

  const getMonthRange = useCallback((yStr, mStr) => {
    const y = Number(yStr);
    const m = Number(mStr);
    if (!y || !m || m < 1 || m > 12) return null;

    const start = dayjs(`${y}-${String(m).padStart(2, "0")}-01`).startOf("month");
    const next = start.add(1, "month");
    return { startISO: start.toISOString(), nextISO: next.toISOString() };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const y = String(dayjs().year());
    const m = String(dayjs().month() + 1);

    setYearInput(y);
    setMonthInput(m);

    setAppliedYear(y);
    setAppliedMonth(m);

    setCtx(null);

    // reset form
    setShowExcuseForm(false);
    setExcuseDescription("");
    setExcuseDay(dayjs().format("YYYY-MM-DD"));
    setSelectedClassIds(new Set());
    setSelectedClassesMap(new Map());
    setLiveClassesDay([]);
    setIsCameraExcuse(false);
    setExcuseFile(null);
  }, [isOpen]);

  // semester desde grade-enrollment
  const fetchSemesterFromEnrollment = useCallback(async () => {
    if (!studentId || !gradeId) return null;

    const url =
      `/grade-enrollments?filters[student][id][$eq]=${Number(studentId)}` +
      `&filters[grade][id][$eq]=${Number(gradeId)}` +
      `&populate[semester]=*` +
      `&pagination[pageSize]=5`;

    const res = await xhr(url);
    const list = res?.data?.data || [];
    const item = list[0];
    if (!item) return null;

    const a = normalizeEntity(item);
    const semesterId = a?.semester?.data?.id ?? a?.semester?.id ?? a?.semester ?? null;

    return semesterId ? Number(semesterId) : null;
  }, [studentId, gradeId]);

  const fetchStudentClassGroups = useCallback(async () => {
    if (!studentId) return [];

    setLoadingStudentGroups(true);
    try {
      const url =
        `/student-groups?filters[student][id][$eq]=${Number(studentId)}` +
        `&populate[class_group]=*` +
        `&pagination[pageSize]=200`;

      const res = await xhr(url);
      const list = res?.data?.data || [];

      const ids = Array.from(
        new Set(
          list
            .map((sg) => {
              const a = sg?.attributes || sg;
              return a?.class_group?.data?.id ?? a?.class_group?.id ?? null;
            })
            .filter(Boolean)
            .map(Number)
        )
      );

      return ids;
    } finally {
      setLoadingStudentGroups(false);
    }
  }, [studentId]);

  useEffect(() => {
    const run = async () => {
      if (!isOpen || !studentId) {
        setStudentClassGroupIds([]);
        return;
      }

      try {
        const ids = await fetchStudentClassGroups();
        setStudentClassGroupIds(ids);
      } catch (e) {
        setStudentClassGroupIds([]);
      }
    };

    run();
  }, [isOpen, studentId, fetchStudentClassGroups]);


  // construir ctx
  useEffect(() => {
    const run = async () => {
      if (!isOpen || !studentId || !gradeId) return;

      setLoadingCtx(true);
      try {
        const semesterId = await fetchSemesterFromEnrollment();

        setCtx({
          gradeId: Number(gradeId),
          semesterId,
        });
      } catch (e) {
        setCtx(null);
      } finally {
        setLoadingCtx(false);
      }
    };

    run();
  }, [isOpen, studentId, gradeId, fetchSemesterFromEnrollment]);

  const fetchAbsences = useCallback(
    async ({ y, m }) => {
      const range = getMonthRange(y, m);
      if (!range) return [];

      const { startISO, nextISO } = range;

      let url =
        `/student-attendances?filters[student][id][$eq]=${studentId}` +
        `&filters[live_class][start_date][$gte]=${encodeURIComponent(startISO)}` +
        `&filters[live_class][start_date][$lt]=${encodeURIComponent(nextISO)}` +
        `&populate[live_class][populate][subject]=*` +
        `&sort[0]=live_class.start_date:desc` +
        `&pagination[pageSize]=9999`;

      if (gradeId) url += `&filters[grade][id][$eq]=${Number(gradeId)}`;
      if (programTypeId) url += `&filters[program_type][id][$eq]=${Number(programTypeId)}`;

      const res = await xhr(url);
      const list = res?.data?.data || [];

      return list.map((item) => {
        const a = item?.attributes || item;
        const { lc, subject } = normalizeLiveClass(item);

        return {
          id: item?.id || a?.id,
          liveClassId: lc?.id || null,
          startDate: lc?.start_date || null,
          subjectName: subject?.name || "—",
          liveClassTitle: lc?.title || "",
        };
      });
    },
    [studentId, gradeId, programTypeId, getMonthRange]
  );

  // excusas por live-classes (mes)
  const fetchExcuses = useCallback(
    async ({ y, m }) => {
      const range = getMonthRange(y, m);
      if (!range) return [];

      const { startISO, nextISO } = range;

      const normalUrl =
        `/student-excuses?filters[student][id][$eq]=${studentId}` +
        `&filters[live_classes][start_date][$gte]=${encodeURIComponent(startISO)}` +
        `&filters[live_classes][start_date][$lt]=${encodeURIComponent(nextISO)}` +
        `&populate[live_classes]=*` +
        `&populate[file]=*` +
        `&sort[0]=createdAt:desc` +
        `&pagination[pageSize]=9999`;

      const cameraUrl =
        `/student-excuse-cameras?filters[student][id][$eq]=${studentId}` +
        `&filters[live_classes][start_date][$gte]=${encodeURIComponent(startISO)}` +
        `&filters[live_classes][start_date][$lt]=${encodeURIComponent(nextISO)}` +
        `&populate[live_classes]=*` +
        `&populate[file]=*` +
        `&sort[0]=createdAt:desc` +
        `&pagination[pageSize]=9999`;

      const [normalRes, cameraRes] = await Promise.all([
        xhr(normalUrl),
        xhr(cameraUrl),
      ]);

      const mapExcuse = (item, type) => {
        const a = normalizeEntity(item);

        const rel = a?.live_classes?.data || a?.live_classes || [];

        const fileData = a?.file?.data?.attributes
          ? {
            id: a.file.data.id,
            ...a.file.data.attributes,
          }
          : a?.file || null;
        const liveClasses = Array.isArray(rel)
          ? rel.map((x) => {
            const ax = x?.attributes ? x.attributes : x;
            return {
              id: x?.id ?? ax?.id,
              title: ax?.title || "",
              start_date: ax?.start_date || null,
            };
          })
          : [];

        return {
          id: item?.id ?? a?.id,
          description: a?.description || "",
          type, // "normal" | "camera"
          createdAt: a?.createdAt || null,
          liveClassIds: liveClasses.map((x) => Number(x.id)).filter(Boolean),
          liveClasses,
          file: fileData,
        };
      };

      const normalList = (normalRes?.data?.data || []).map((item) =>
        mapExcuse(item, "normal")
      );

      const cameraList = (cameraRes?.data?.data || []).map((item) =>
        mapExcuse(item, "camera")
      );

      return [...normalList, ...cameraList].sort((a, b) => {
        const da = a?.createdAt || "";
        const db = b?.createdAt || "";
        return da < db ? 1 : -1;
      });
    },
    [studentId, getMonthRange]
  );

  // ✅ live-classes SOLO por día (para el form)
  const getDayRange = useCallback((yyyyMmDd) => {
    const d = dayjs(yyyyMmDd);
    return {
      startISO: d.startOf("day").toISOString(),
      endISO: d.endOf("day").toISOString(),
    };
  }, []);

  const fetchLiveClassesByDay = useCallback(
    async (yyyyMmDd) => {
      const currentProgramId = Number(gradeId);

      if (!yyyyMmDd || !currentProgramId) return [];

      const { startISO, endISO } = getDayRange(yyyyMmDd);

      const url =
        `/live-classes?filters[start_date][$gte]=${encodeURIComponent(startISO)}` +
        `&filters[start_date][$lte]=${encodeURIComponent(endISO)}` +
        `&filters[$or][0][grade][id][$eq]=${currentProgramId}` +
        `&filters[$or][1][course][id][$eq]=${currentProgramId}` +
        `&filters[$or][2][technical_career][id][$eq]=${currentProgramId}` +
        `&populate[subject]=*` +
        `&populate[class_group]=*` +
        `&sort[0]=start_date:asc` +
        `&pagination[pageSize]=50`;

      const res = await xhr(url);
      const list = res?.data?.data || [];

      const allowedGroups = new Set((studentClassGroupIds || []).map(Number));

      const mapped = list.map((lc) => {
        const a = normalizeEntity(lc);

        const classGroupId =
          a?.class_group?.data?.id ?? a?.class_group?.id ?? null;

        return {
          id: lc?.id ?? a?.id,
          title: a?.title || "",
          start_date: a?.start_date || null,
          subjectName:
            a?.subject?.data?.attributes?.name ?? a?.subject?.name ?? "—",
          classGroupId: classGroupId ? Number(classGroupId) : null,
        };
      });

      if (allowedGroups.size > 0) {
        return mapped.filter(
          (x) => x.classGroupId && allowedGroups.has(x.classGroupId)
        );
      }

      return mapped;
    },
    [gradeId, getDayRange, studentClassGroupIds]
  );
  // Cargar datos (solo absences + excuses)
  useEffect(() => {
    const run = async () => {
      if (!isOpen || !studentId) return;

      setLoading(true);
      setLoadingExcuses(true);
      setAbsences([]);
      setExcuses([]);

      try {
        const [abs, exc] = await Promise.all([
          fetchAbsences({ y: appliedYear, m: appliedMonth }),
          fetchExcuses({ y: appliedYear, m: appliedMonth }),
        ]);

        setAbsences(abs);
        setExcuses(exc);
      } catch (e) {
        toast({
          title: "No se pudo cargar",
          description:
            "Revisa permisos del role o endpoints (/student-attendances, /student-excuses).",
          status: "error",
          duration: 4500,
          isClosable: true,
        });
      } finally {
        setLoading(false);
        setLoadingExcuses(false);
      }
    };

    run();
  }, [isOpen, studentId, appliedYear, appliedMonth, fetchAbsences, fetchExcuses, toast]);

  const handleSearch = () => {
    setAppliedYear(yearInput);
    setAppliedMonth(monthInput);
  };

  // excusada por relación (liveClassId ∈ excuse.liveClassIds)
  const isExcused = useCallback(
    (absence) => {
      const lcId = Number(absence?.liveClassId);
      if (!lcId) return false;
      return excuses.some((ex) => (ex?.liveClassIds || []).includes(lcId));
    },
    [excuses]
  );

  const deleteAbsence = async (absenceId) => {
    if (!absenceId) return;

    const ok = window.confirm("¿Seguro que deseas eliminar esta inasistencia?");
    if (!ok) return;

    setDeletingAbsId(absenceId);
    try {
      await xhr(`/student-attendances/${absenceId}`, { method: "DELETE" });
      setAbsences((prev) => prev.filter((x) => x.id !== absenceId));

      toast({
        title: "Inasistencia eliminada",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "No se pudo eliminar",
        description: "Revisa permisos del role o que el ID exista.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingAbsId(null);
    }
  };

  // ✅ clases del día seleccionado en el form
  const classesForExcuseDay = liveClassesDay || [];

  // ✅ resumen de seleccionadas (desde el cache)
  const selectedClasses = useMemo(() => {
    return Array.from(selectedClassesMap.values()).sort((a, b) =>
      a?.start_date > b?.start_date ? 1 : -1
    );
  }, [selectedClassesMap]);

  const selectedByDay = useMemo(() => {
    const map = new Map();

    selectedClasses.forEach((c) => {
      const day = c.start_date ? dayjs(c.start_date).format("YYYY-MM-DD") : "—";
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(c);
    });

    return Array.from(map.entries())
      .map(([day, classes]) => ({
        day,
        classes: classes.sort((a, b) => (a.start_date > b.start_date ? 1 : -1)),
      }))
      .sort((a, b) => (a.day > b.day ? 1 : -1));
  }, [selectedClasses]);

  const toggleClass = (cls) => {
    const id = Number(cls?.id);
    if (!id) return;

    // Set ids
    setSelectedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

    // Cache objects (so multi-day selection persists)
    setSelectedClassesMap((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, cls);
      return next;
    });
  };

  const removeSelectedClass = (id) => {
    const key = Number(id);

    setSelectedClassIds((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

    setSelectedClassesMap((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const clearSelected = () => {
    setSelectedClassIds(new Set());
    setSelectedClassesMap(new Map());
  };

  const createExcuse = async () => {
    if (!studentId) return;

    if (!excuseDescription.trim()) {
      toast({
        title: "Escribe un título",
        description: "Ej: Cita médica",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const ids = Array.from(selectedClassIds);
    if (ids.length === 0) {
      toast({
        title: "Selecciona al menos una clase",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const endpoint = isCameraExcuse
      ? "/student-excuse-cameras"
      : "/student-excuses";

    const payload = {
      data: {
        student: Number(studentId),
        description: excuseDescription.trim(),
        live_classes: ids,
      },
    };

    setSavingExcuse(true);
    try {
      const response = await xhr.post(endpoint, payload);

      if (excuseFile) {
        const formData = new FormData();

        formData.append("files", excuseFile);
        formData.append(
          "ref",
          isCameraExcuse
            ? "api::student-excuse-camera.student-excuse-camera"
            : "api::student-excuse.student-excuse"
        );
        formData.append("refId", response.data.data.id);
        formData.append("field", "file");

        await xhr("/upload", {
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast({
        title: isCameraExcuse
          ? "Excusa por cámara registrada"
          : "Excusa registrada",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowExcuseForm(false);
      setExcuseDescription("");
      setExcuseDay(dayjs().format("YYYY-MM-DD"));
      setSelectedClassIds(new Set());
      setSelectedClassesMap(new Map());
      setLiveClassesDay([]);
      setIsCameraExcuse(false);
      setExcuseFile(null);

      setLoadingExcuses(true);
      const exc = await fetchExcuses({ y: appliedYear, m: appliedMonth });
      setExcuses(exc);
    } catch (e) {
      toast({
        title: "No se pudo registrar la excusa",
        description: isCameraExcuse
          ? "Verifica la relación live_classes en student-excuse-cameras y permisos del role."
          : "Verifica la relación live_classes en student-excuses y permisos del role.",
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSavingExcuse(false);
      setLoadingExcuses(false);
    }
  };

  const disableClose = !!deletingAbsId || savingExcuse;

  return (
    <Modal isOpen={isOpen} onClose={disableClose ? () => { } : onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Inasistencias del estudiante</ModalHeader>
        <ModalCloseButton disabled={disableClose} />

        <ModalBody>
          {programName ? (
            <Text fontSize="12px" color="gray.600" mb={2}>
              Programa: <b>{programName}</b>
            </Text>
          ) : null}

          <Flex gap={3} align="center" wrap="wrap" mb={3}>
            <Box>
              <Text fontSize="12px" color="gray.600" mb={1}>
                Año
              </Text>
              <Select
                width="120px"
                value={yearInput}
                onChange={(e) => setYearInput(e.target.value)}
                isDisabled={loading}
              >
                {yearsOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="12px" color="gray.600" mb={1}>
                Mes
              </Text>
              <Select
                width="180px"
                value={monthInput}
                onChange={(e) => setMonthInput(e.target.value)}
                isDisabled={loading}
              >
                {monthsOptions.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Box>

            <Button mt="18px" size="sm" colorScheme="blue" onClick={handleSearch} isLoading={loading}>
              Buscar
            </Button>

            <Text mt="18px" fontSize="12px" color="gray.600">
              Aplicado: {appliedYear}-{String(appliedMonth).padStart(2, "0")}
            </Text>

            {(loading || loadingExcuses || loadingCtx || loadingStudentGroups) && (
              <Flex mt="18px" align="center" gap={2}>
                <Spinner size="sm" />
                <Text fontSize="12px">Cargando...</Text>
              </Flex>
            )}
          </Flex>

          {loading ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : absences.length === 0 ? (
            <Text>No hay inasistencias en este mes para este programa.</Text>
          ) : (
            <TableContainer border="1px solid" borderColor="gray.200" rounded="md">
              <Table size="sm">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>FECHA</Th>
                    <Th>CLASE</Th>
                    <Th width="180px">ACCIÓN</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {absences.map((a) => {
                    const excused = isExcused(a);

                    return (
                      <Tr key={a.id}>
                        <Td>{a.startDate ? dayjs(a.startDate).format("YYYY-MM-DD") : "—"}</Td>
                        <Td>{a.liveClassTitle || "—"}</Td>
                        <Td>
                          {excused ? (
                            <Flex align="center" gap={2}>
                              <Button size="xs" isDisabled colorScheme="gray">
                                Excusada
                              </Button>
                              <Tag size="sm" colorScheme="green">
                                OK
                              </Tag>
                            </Flex>
                          ) : (
                            <Button
                              size="xs"
                              colorScheme="red"
                              isLoading={deletingAbsId === a.id}
                              onClick={() => deleteAbsence(a.id)}
                            >
                              Eliminar
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          <Divider my={4} />
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontWeight="600">Excusas</Text>
            <Button
              size="sm"
              colorScheme="purple"
              onClick={async () => {
                const opening = !showExcuseForm;
                setShowExcuseForm(opening);

                if (opening) {
                  const today = dayjs().format("YYYY-MM-DD");
                  setExcuseDay(today);

                  setLoadingLiveClassesDay(true);
                  try {
                    const list = await fetchLiveClassesByDay(today);
                    setLiveClassesDay(list);
                  } finally {
                    setLoadingLiveClassesDay(false);
                  }
                } else {
                  // closing
                  setLiveClassesDay([]);
                }
              }}
            >
              {showExcuseForm ? "Cancelar" : "Registrar excusa"}
            </Button>
          </Flex>

          {loadingExcuses ? (
            <Flex align="center" gap={2} py={2}>
              <Spinner size="sm" />
              <Text fontSize="12px">Cargando excusas...</Text>
            </Flex>
          ) : excuses.length === 0 ? (
            <Text fontSize="sm" color="gray.600" mb={3}>
              No hay excusas registradas en este mes.
            </Text>
          ) : (
            <Box border="1px solid" borderColor="gray.200" rounded="md" overflow="hidden" mb={3}>
              {excuses.map((ex) => (
                <Box
                  key={`${ex.type}-${ex.id}`}
                  px={3}
                  py={2}
                  borderTopWidth="1px"
                  _first={{ borderTopWidth: 0 }}
                >
                  <Flex justify="space-between" align="center" gap={2} mb={1}>
                    <Text fontWeight="600" fontSize="sm">
                      {ex.description?.trim() ? ex.description : "Sin título"}
                    </Text>

                    <Tag
                      size="sm"
                      colorScheme={ex.type === "camera" ? "purple" : "blue"}
                    >
                      {ex.type === "camera" ? "Excusa por cámara" : "Excusa normal"}
                    </Tag>
                  </Flex>

                  {(ex.liveClasses || []).length ? (
                    <Text fontSize="12px" color="gray.600">
                      {(ex.liveClasses || [])
                        .filter((c) => c.start_date)
                        .sort((a, b) => (a.start_date > b.start_date ? 1 : -1))
                        .slice(0, 3)
                        .map((c) => dayjs(c.start_date).format("YYYY-MM-DD HH:mm"))
                        .join(" · ")}
                      {(ex.liveClasses || []).length > 3 ? " · ..." : ""}
                    </Text>
                  ) : (
                    <Text fontSize="12px" color="gray.600">
                      (Sin clases relacionadas)
                    </Text>
                  )}

                  {ex.file?.url && (
                    <Box mt={2}>
                      <Button
                        as="a"
                        href={ex.file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        colorScheme="blue"
                        variant="outline"
                      >
                        Ver soporte
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {showExcuseForm && (
            <Box border="1px solid" borderColor="gray.200" rounded="md" p={3}>
              <Box mb={3}>
                <Flex align="center" gap={3}>
                  <Switch
                    isChecked={isCameraExcuse}
                    onChange={(e) => setIsCameraExcuse(e.target.checked)}
                    isDisabled={savingExcuse}
                    colorScheme="purple"
                  />
                  <Text fontSize="sm" fontWeight="600">
                    Excusa por cámara
                  </Text>
                </Flex>

                <Text fontSize="12px" color="gray.600" mt={1}>
                  {isCameraExcuse
                    ? "La excusa se guardará como excusa por cámara."
                    : "La excusa se guardará como excusa normal."}
                </Text>
              </Box>

              <Box mb={3}>
                <Text fontSize="12px" color="gray.600" mb={1}>
                  Título / descripción
                </Text>
                <Input
                  placeholder={
                    isCameraExcuse ? "Ej: Cámara dañada" : "Ej: Cita médica"
                  }
                  value={excuseDescription}
                  onChange={(e) => setExcuseDescription(e.target.value)}
                  isDisabled={savingExcuse}
                />
              </Box>

              <Box mb={3}>
                <Text fontSize="12px" color="gray.600" mb={1}>
                  Soporte en PDF o imagen opcional
                </Text>

                <Box
                  {...getExcuseRootProps({ className: "dropzone" })}
                  backgroundColor="gray.50"
                  borderRadius="8px"
                  padding="12px"
                  cursor={savingExcuse ? "not-allowed" : "pointer"}
                  opacity={savingExcuse ? 0.6 : 1}
                  border="1px dashed"
                  borderColor="gray.300"
                  _hover={{
                    backgroundColor: savingExcuse ? "gray.50" : "gray.100",
                  }}
                >
                  <Input {...getExcuseInputProps()} isDisabled={savingExcuse} />

                  <Flex align="center" gap={3}>
                    <Box fontSize="26px" color="gray.600">
                      <BiCloudUpload />
                    </Box>

                    <Box>
                      <Text fontSize="sm" fontWeight="600">
                        Arrastra el archivo o haz clic para seleccionarlo
                      </Text>
                      <Text fontSize="12px" color="gray.500">
                        Solo PDF o imágenes JPG, PNG o WEBP. Este soporte es opcional.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                {excuseFile && (
                  <Flex
                    justify="space-between"
                    align="center"
                    gap={2}
                    mt={2}
                    px={3}
                    py={2}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    backgroundColor="gray.50"
                  >
                    <Text fontWeight="500" fontSize="sm" noOfLines={1}>
                      {excuseFile.name}
                    </Text>

                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => setExcuseFile(null)}
                      isDisabled={savingExcuse}
                    >
                      Quitar
                    </Button>
                  </Flex>
                )}
              </Box>

              {/* 3. SELECCIÓN DEL DÍA */}
              <Box mb={3}>
                <Text fontSize="12px" color="gray.600" mb={1}>
                  Selecciona el día (puedes cambiarlo y seguir agregando)
                </Text>

                <Input
                  type="date"
                  value={excuseDay}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setExcuseDay(val);

                    setLoadingLiveClassesDay(true);
                    try {
                      const list = await fetchLiveClassesByDay(val);
                      setLiveClassesDay(list);
                    } finally {
                      setLoadingLiveClassesDay(false);
                    }
                  }}
                  isDisabled={savingExcuse}
                />
              </Box>

              <Box mb={3}>
                <Text fontSize="12px" color="gray.600" mb={2}>
                  Clases en vivo del día seleccionado
                </Text>

                {loadingLiveClassesDay ? (
                  <Flex align="center" gap={2}>
                    <Spinner size="sm" />
                    <Text fontSize="12px">Cargando clases...</Text>
                  </Flex>
                ) : classesForExcuseDay.length === 0 ? (
                  <Text fontSize="sm" color="gray.600">
                    No hay clases en vivo para ese día.
                  </Text>
                ) : (
                  <Stack spacing={2}>
                    {classesForExcuseDay.map((c) => (
                      <Checkbox
                        key={c.id}
                        isChecked={selectedClassIds.has(Number(c.id))}
                        onChange={() => toggleClass(c)}
                        isDisabled={savingExcuse}
                      >
                        <Text fontSize="sm" fontWeight="600">
                          {c.start_date ? dayjs(c.start_date).format("HH:mm") : "—"} ·{" "}
                          {c.title || "Clase"}
                        </Text>
                        <Text fontSize="12px" color="gray.600">
                          {c.subjectName}
                        </Text>
                      </Checkbox>
                    ))}
                  </Stack>
                )}
              </Box>

              <Flex justify="flex-end" mb={3}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSelected}
                  isDisabled={savingExcuse || selectedClassIds.size === 0}
                >
                  Limpiar selección
                </Button>
              </Flex>

              <Box>
                <Text fontSize="12px" color="gray.600" mb={2}>
                  Clases en vivo del día seleccionado
                </Text>
              </Box>

              <Divider my={4} />

              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="600" fontSize="sm">
                  Clases seleccionadas ({selectedClassIds.size})
                </Text>
              </Flex>

              {selectedByDay.length === 0 ? (
                <Text fontSize="sm" color="gray.600">
                  Aún no has seleccionado clases. Elige un día y marca una o más clases.
                </Text>
              ) : (
                <Box border="1px solid" borderColor="gray.200" rounded="md" overflow="hidden">
                  {selectedByDay.map((group) => (
                    <Box key={group.day} borderTopWidth="1px" _first={{ borderTopWidth: 0 }}>
                      <Box px={3} py={2} bg="gray.50">
                        <Text fontWeight="700" fontSize="sm">
                          {group.day}
                        </Text>
                      </Box>

                      {group.classes.map((c) => (
                        <Flex
                          key={c.id}
                          px={3}
                          py={2}
                          borderTopWidth="1px"
                          _first={{ borderTopWidth: 0 }}
                          justify="space-between"
                          align="center"
                          gap={3}
                        >
                          <Box>
                            <Text fontWeight="600" fontSize="sm">
                              {c.start_date ? dayjs(c.start_date).format("HH:mm") : "—"} ·{" "}
                              {c.title || "Clase"}
                            </Text>
                            <Text fontSize="12px" color="gray.600">
                              {c.subjectName}
                            </Text>
                          </Box>

                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => removeSelectedClass(c.id)}
                            isDisabled={savingExcuse}
                          >
                            Quitar
                          </Button>
                        </Flex>
                      ))}
                    </Box>
                  ))}
                </Box>
              )}


              <Flex justify="flex-end" mt={4} gap={2}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowExcuseForm(false);
                    setLiveClassesDay([]);
                    setIsCameraExcuse(false);
                    setExcuseFile(null);
                  }}
                  isDisabled={savingExcuse}
                >
                  Cancelar
                </Button>
                <Button
                  colorScheme="purple"
                  onClick={createExcuse}
                  isLoading={savingExcuse}
                  isDisabled={selectedClassIds.size === 0}
                >
                  {isCameraExcuse ? "Guardar excusa por cámara" : "Guardar excusa"}
                </Button>
              </Flex>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose} isDisabled={disableClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}